import { z } from "zod/v3";

// Date type matching the DateSelect component format
const DateSchema = z.object({
  startDate: z.string(),
  endDate: z.string()
});

// End date is optional - accepts cleared state { startDate: null, endDate: null } from DateSelect
const OptionalEndDateSchema = z
  .object({
    startDate: z.union([z.string(), z.null()]).optional(),
    endDate: z.union([z.string(), z.null()]).optional()
  })
  .optional();

export const createActivitySchema = z
  .object({
    title: z.string().min(1, "Title is required"),
    activityType: z.object({
      id: z.string().min(1, "Activity Type is required"),
      name: z.string().min(1, "Activity Type is required")
    }),
    sectionId: z.string().min(1, "Managing Section is required"),
    section: z.string().optional(),
    activityDescription: z.string().optional(),
    // Step 2 - Activity Dates fields
    duration: z
      .string()
      .min(1, "Activity Duration is required")
      .refine(
        (val) => {
          if (!val || val === "") return true;
          const num = parseFloat(val);
          if (isNaN(num) || num <= 0) return false;
          const decimalPart = val.split(".")[1];
          return !decimalPart || decimalPart.length <= 1;
        },
        { message: "Duration must be a positive number with up to one decimal place" }
      ),
    startDate: DateSchema.refine((data) => data.startDate && data.endDate, {
      message: "Start date is required"
    }),
    endDate: OptionalEndDateSchema,
    // Step 3 - Modules fields
    checkinEnabled: z.boolean().default(false)
  })
  .refine(
    (data) => {
      const start = data.startDate?.startDate;
      const end = data.endDate?.startDate;
      if (!start || !end) return true;
      return new Date(start) <= new Date(end);
    },
    { message: "Start date cannot be later than end date", path: ["endDate"] }
  );

export type FormValues = z.infer<typeof createActivitySchema>;
